import React from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  CharCounter,
  ErrorMessage,
  FormGroup,
  Label,
  NoticeBox,
  StyledInput,
  StyledTextarea,
} from '@/pages/Seminar/components/form/formStyles';
import { ONLINE_GUIDE_MAX_LENGTH, SeminarFormatValue } from '@/pages/Seminar/components/form/seminarFormUtils';

interface OnlineAccessFieldsProps {
  format: Exclude<SeminarFormatValue, 'OFFLINE'>;
  url: string;
  guide: string;
  onUrlChange: (value: string) => void;
  onGuideChange: (value: string) => void;
  urlError?: string;
  guideError?: string;
  /** 상시 동영상 — 시청 시작·종료 알림이 없다 */
  openSale?: boolean;
}

/**
 * 라이브·동영상 접속 정보 — 링크(https 필수)와 안내(선택 300자).
 * 결제(신청 확정)한 참가자에게만 보인다는 고지를 섹션 아래에 둔다.
 */
const OnlineAccessFields: React.FC<OnlineAccessFieldsProps> = ({
  format,
  url,
  guide,
  onUrlChange,
  onGuideChange,
  urlError,
  guideError,
  openSale = false,
}) => {
  const isVod: boolean = format === 'VOD';
  const noun: string = isVod ? '시청' : '접속';

  return (
    <>
      <FormGroup>
        <Label htmlFor="seminar-online-url">
          {noun} 링크<span>*</span>
        </Label>
        <StyledInput
          id="seminar-online-url"
          name="onlineUrl"
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder={isVod ? 'https://band.us/band/12345678' : 'https://zoom.us/j/1234567890'}
          value={url}
          onChange={(e) => onUrlChange(e.target.value.trim())}
          data-field="onlineUrl"
          aria-invalid={!!urlError}
          hasError={!!urlError}
        />
        {urlError && <ErrorMessage>{urlError}</ErrorMessage>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="seminar-online-guide">{noun} 안내</Label>
        <StyledTextarea
          id="seminar-online-guide"
          name="onlineGuide"
          maxLength={ONLINE_GUIDE_MAX_LENGTH}
          placeholder={
            isVod
              ? '예) BAND 가입 신청 후 승인되면 시청할 수 있어요'
              : '예) 회의 비밀번호는 시작 1시간 전 알림으로 보내요'
          }
          value={guide}
          onChange={(e) => onGuideChange(e.target.value.slice(0, ONLINE_GUIDE_MAX_LENGTH))}
          data-field="onlineGuide"
          aria-invalid={!!guideError}
          hasError={!!guideError}
        />
        <CharCounter over={guide.length > ONLINE_GUIDE_MAX_LENGTH}>
          {guide.length}/{ONLINE_GUIDE_MAX_LENGTH}
        </CharCounter>
        {guideError && <ErrorMessage>{guideError}</ErrorMessage>}
      </FormGroup>

      <NoticeBox>
        <LockOutlinedIcon aria-hidden />
        <span>
          {openSale
            ? '링크와 안내는 결제(신청 확정)한 참가자에게만 보여요.\n링크를 바꾸면 참가자에게 알림이 가요.\n상시 동영상은 결제하면 바로 볼 수 있어 시청 알림은 따로 가지 않아요.'
            : isVod
            ? '링크와 안내는 결제(신청 확정)한 참가자에게만 보여요.\n링크를 바꾸면 참가자에게 알림이 가요.\n시청 시작일과 종료 3일 전에 참가자에게 알림이 가요.'
            : '링크와 안내는 결제(신청 확정)한 참가자에게만 보여요.\n링크를 바꾸면 참가자에게 알림이 가요.\n시작 1시간 전에 참가자에게 접속 알림이 가요.'}
        </span>
      </NoticeBox>
    </>
  );
};

export default OnlineAccessFields;
